/**
 * Sprint 8 Day-3 — /signals/sources page.
 *
 * Three live source tiles (Reddit / Quora / LinkedIn Groups) + 8 "Coming in
 * v2" tiles with notify-me votes. Per-source: pause/resume, keyword editing,
 * autonomy-mode toggle (advisor ↔ autonomous), delete.
 */
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { MessageCircle, Send, Linkedin, Plus, Trash2, Play, Pause, RefreshCw, ArrowLeft, Bell, CheckCircle2, ShieldAlert, Twitter, Facebook, Instagram, Globe, Megaphone } from 'lucide-react';
import { toast } from "sonner";
import { signalsApi } from "@/lib/api";
import { useAssistantName } from "@/hooks/useAssistantName";

const LIVE_SOURCES = [
  { id: "reddit",         label: "Reddit",          Icon: MessageCircle,
    ring: "text-orange-700 bg-orange-50 ring-orange-200",
    placeholder: "r/SaaS",
    hint:        "Subreddit handle (e.g. r/startups)",
    keywordsExample: "pricing, alternative, AI SDR" },
  { id: "quora",          label: "Quora",           Icon: Send,
    ring: "text-red-700 bg-red-50 ring-red-200",
    placeholder: "Pricing Strategy",
    hint:        "Quora topic name",
    keywordsExample: "SaaS, B2B, pricing model" },
  { id: "linkedin_group", label: "LinkedIn Groups", Icon: Linkedin,
    ring: "text-blue-700 bg-blue-50 ring-blue-200",
    placeholder: "SaaS Founders Network",
    hint:        "Group name (you must be a member)",
    keywordsExample: "hiring, fundraising, scale" },
  { id: "x",              label: "X (Twitter)",     Icon: Twitter,
    ring: "text-slate-800 bg-slate-100 ring-slate-300",
    placeholder: "#saas OR \"outreach.io\"",
    hint:        "Hashtag, search query, or list URL",
    keywordsExample: "AI SDR, cold email, pricing" },
  { id: "facebook",       label: "Facebook",        Icon: Facebook,
    ring: "text-blue-800 bg-blue-50 ring-blue-200",
    placeholder: "Page or Group URL / name",
    hint:        "Facebook Page or Group to monitor",
    keywordsExample: "demo, pricing, looking for" },
  { id: "instagram",      label: "Instagram",       Icon: Instagram,
    ring: "text-pink-700 bg-pink-50 ring-pink-200",
    placeholder: "@brand or hashtag",
    hint:        "Instagram handle or hashtag",
    keywordsExample: "agency, hire, collab" },
  { id: "google_ads",     label: "Google Ads",      Icon: Megaphone,
    ring: "text-amber-800 bg-amber-50 ring-amber-200",
    placeholder: "Campaign name or account label",
    hint:        "Google Ads campaign / lead-form source",
    keywordsExample: "demo request, contact" },
  { id: "website",        label: "Website",         Icon: Globe,
    ring: "text-emerald-800 bg-emerald-50 ring-emerald-200",
    placeholder: "https://yoursite.com/contact",
    hint:        "Landing / contact / demo form URL",
    keywordsExample: "demo, quote, contact" },
];

function groupSourcesByChannel(sources) {
  return sources.reduce((acc, s) => {
    if (!acc[s.source]) {
      acc[s.source] = [];
    }
    acc[s.source].push(s);
    return acc;
  }, {});
}

function Badge2({ paused, mode }) {
  return (
    <div className="hidden sm:flex items-center gap-1 text-[10px]">
      <span className={cn("rounded-full px-2 py-0.5 ring-1 font-semibold",
        paused ? "bg-slate-100 text-slate-600 ring-slate-200"
               : "bg-emerald-50 text-emerald-700 ring-emerald-200")}>
        {paused ? "Paused" : "Live"}
      </span>
      <span className="rounded-full px-2 py-0.5 ring-1 font-medium bg-slate-50 text-slate-600 ring-slate-200">
        {mode === "autonomous" ? "Auto" : "Advisor"}
      </span>
    </div>
  );
}
Badge2.propTypes = {
  mode: PropTypes.string,
  paused: PropTypes.bool,
};

function SourceForm({ channel, onCreate, onCancel }) {
  const [community, setCommunity] = useState("");
  const [keywords, setKeywords] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit() {
    if (!community.trim()) return toast.error("Pick a community");
    setBusy(true);
    try {
      await onCreate({
        source: channel.id,
        community: community.trim(),
        keywords: keywords.split(",").map((s) => s.trim()).filter(Boolean),
      });
      setCommunity(""); setKeywords("");
    } catch (e) {
      toast.error(e.message || "Couldn't add source");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 px-4 sm:px-5 py-4" data-testid={`source-form-${channel.id}`}>
      <div className="flex items-center gap-2 text-[12.5px] font-semibold text-slate-900">
        <channel.Icon size={13} /> Add a {channel.label} source
      </div>
      <div className="mt-3 grid sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor={`source-community-input-${channel.id}`} className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">{channel.hint}</label>
          <Input id={`source-community-input-${channel.id}`} className="mt-1 h-9 text-[13px]" placeholder={channel.placeholder}
            value={community} onChange={(e) => setCommunity(e.target.value)}
            data-testid={`source-community-input-${channel.id}`} />
        </div>
        <div>
          <label htmlFor={`source-keywords-input-${channel.id}`} className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Keywords (comma-separated)</label>
          <Input id={`source-keywords-input-${channel.id}`} className="mt-1 h-9 text-[13px]" placeholder={channel.keywordsExample}
            value={keywords} onChange={(e) => setKeywords(e.target.value)}
            data-testid={`source-keywords-input-${channel.id}`} />
        </div>
      </div>
      <div className="mt-3 flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2">
        <Button size="sm" variant="ghost" className="h-10 sm:h-8 text-[11.5px] w-full sm:w-auto" onClick={onCancel}>Cancel</Button>
        <Button size="sm" className="h-10 sm:h-8 text-[11.5px] w-full sm:w-auto" onClick={submit} disabled={busy}
          data-testid={`source-create-${channel.id}`}>
          <Plus size={11} /> Add source
        </Button>
      </div>
    </div>
  );
}

SourceForm.propTypes = {
  channel: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    Icon: PropTypes.elementType,
    hint: PropTypes.string,
    placeholder: PropTypes.string,
    keywordsExample: PropTypes.string,
  }).isRequired,
  onCreate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

function SourceRow({ source, onReload }) {
  const meta = LIVE_SOURCES.find((c) => c.id === source.source) || LIVE_SOURCES[0];
  const [busy, setBusy] = useState(false);
  async function toggle() {
    setBusy(true);
    try {
      await (source.paused ? signalsApi.resumeSource(source.id)
                           : signalsApi.pauseSource(source.id));
      onReload();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }
  async function remove() {
    if (!window.confirm(`Remove ${source.display_name}?`)) return;
    setBusy(true);
    try { await signalsApi.deleteSource(source.id); onReload(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }
  async function pollNow() {
    setBusy(true);
    try {
      await signalsApi.pollNow(source.id);
      toast.success("Queued for next poller tick");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 sm:px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center min-w-0"
      data-testid={`source-row-${source.id}`}>
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <span className={cn("inline-flex h-7 w-7 items-center justify-center rounded-lg ring-1 shrink-0", meta.ring)}>
          <meta.Icon size={13} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-slate-900 truncate">{source.display_name}</div>
          <div className="text-[10.5px] text-slate-500 break-words sm:truncate">
            {source.keywords.length} keywords · {source.daily_budget_polls} polls/day · {source.cooldown_minutes / 60}h cooldown
            {!source.membership_verified && (
              <span className="ml-2 inline-flex items-center gap-0.5 text-amber-700 font-medium">
                <ShieldAlert size={9} /> membership unverified
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between sm:justify-end gap-1 shrink-0">
        <Badge2 paused={source.paused} mode={source.autonomy_mode} />
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" className="h-10 w-10 sm:h-8 sm:w-8 sm:px-2" onClick={toggle} disabled={busy}
            data-testid={`source-toggle-${source.id}`} aria-label={source.paused ? "Resume source" : "Pause source"}>
            {source.paused ? <Play size={12} /> : <Pause size={12} />}
          </Button>
          <Button size="sm" variant="ghost" className="h-10 w-10 sm:h-8 sm:w-8 sm:px-2 text-slate-500" onClick={pollNow} disabled={busy || source.paused}
            data-testid={`source-pollnow-${source.id}`} title="Poll now" aria-label="Poll now">
            <RefreshCw size={12} />
          </Button>
          <Button size="sm" variant="ghost" className="h-10 w-10 sm:h-8 sm:w-8 sm:px-2 text-rose-500 hover:text-rose-600" onClick={remove} disabled={busy}
            data-testid={`source-delete-${source.id}`} aria-label="Delete source">
            <Trash2 size={12} />
          </Button>
        </div>
      </div>
    </div>
  );
}

SourceRow.propTypes = {
  onReload: PropTypes.func.isRequired,
  source: PropTypes.shape({
    autonomy_mode: PropTypes.string,
    cooldown_minutes: PropTypes.number,
    daily_budget_polls: PropTypes.number,
    display_name: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    keywords: PropTypes.arrayOf(PropTypes.string),
    membership_verified: PropTypes.bool,
    paused: PropTypes.bool,
    source: PropTypes.string,
  }).isRequired,
};

function V2Tile({ tile, voted, onVote }) {
  return (
    <button onClick={onVote} disabled={voted}
      className={cn(
        "rounded-xl border px-4 py-4 text-left transition-all",
        voted ? "border-emerald-200 bg-emerald-50/40"
              : "border-dashed border-slate-200 bg-slate-50/40 hover:bg-white hover:border-slate-300")}
      data-testid={`v2-tile-${tile.id}`}>
      <div className="flex items-center justify-between">
        <div className="text-[13px] font-semibold text-slate-900">{tile.label}</div>
        <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">v2</span>
      </div>
      <div className="mt-2 text-[10.5px] text-slate-500 flex items-center gap-1">
        {voted ? <><CheckCircle2 size={10} className="text-emerald-600" /> You voted</>
               : <><Bell size={10} /> {tile.votes} interested</>}
      </div>
    </button>
  );
}
V2Tile.propTypes = {
  onVote: PropTypes.func.isRequired,
  tile: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    votes: PropTypes.number,
  }).isRequired,
  voted: PropTypes.bool,
};

export default function SignalsSourcesPage() {
  const nav = useNavigate();
  const agentName = useAssistantName();
  const [state, setState] = useState({ loading: true, data: null });
  const [v2, setV2] = useState({ loading: true, sources: [] });
  const [voted, setVoted] = useState(new Set());
  const [addingFor, setAddingFor] = useState(null);
  const [prefs, setPrefs] = useState({ auto_convert_on_approve: false, loading: true });

  async function load() {
    setState((s) => ({ ...s, loading: true }));
    try {
      const [d, v, p] = await Promise.all([
        signalsApi.listSources(),
        signalsApi.listV2Interest(),
        signalsApi.getPreferences().catch(() => ({ auto_convert_on_approve: false })),
      ]);
      setState({ loading: false, data: d });
      setV2({ loading: false, sources: v.sources });
      setPrefs({ auto_convert_on_approve: !!p.auto_convert_on_approve, loading: false });
    } catch (e) { toast.error(e.message); setState({ loading: false, data: null }); }
  }

  useEffect(() => { queueMicrotask(load); }, []);

  async function toggleAutoConvert(next) {
    try {
      const p = await signalsApi.putPreferences({ auto_convert_on_approve: next });
      setPrefs({ auto_convert_on_approve: !!p.auto_convert_on_approve, loading: false });
      toast.success(next ? "Auto-convert on approve enabled" : "Auto-convert disabled — use Convert to RNC manually");
    } catch (e) {
      toast.error(e.message || "Could not update preference");
    }
  }

  async function createSource(body) {
    await signalsApi.createSource(body);
    setAddingFor(null);
    toast.success(`Added — resume to start polling`);
    await load();
  }

  async function voteFor(sid) {
    if (voted.has(sid)) return;
    try {
      await signalsApi.voteV2(sid);
      setVoted((s) => new Set([...s, sid]));
      toast.success("Vote recorded — we'll email you when it ships");
      const v = await signalsApi.listV2Interest();
      setV2({ loading: false, sources: v.sources });
    } catch (e) { toast.error(e.message); }
  }

  const grouped = groupSourcesByChannel(state.data?.sources || []);

  return (
    <div className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden scrollbar-thin bg-white" data-testid="signals-sources-page">
      <div className="max-w-[960px] mx-auto w-full px-4 sm:px-8 py-6 sm:py-10 space-y-6 sm:space-y-8 min-w-0">

        <header className="min-w-0">
          <Button variant="ghost" size="sm" className="h-9 sm:h-8 text-[11.5px] text-slate-500 -ml-2 sm:-ml-3" onClick={() => nav("/signals")}>
            <ArrowLeft size={11} /> Back to feed
          </Button>
          <h1 className="mt-1 text-[24px] sm:text-[28px] font-semibold tracking-tight text-slate-900">Signal sources</h1>
          <p className="mt-1.5 text-[13px] sm:text-[13.5px] text-slate-500 max-w-[640px] leading-relaxed">
            Pick the communities you want {agentName} to watch via OpenClaw. New sources start paused — flip them live when you&apos;re ready.
          </p>
          <label className="mt-4 inline-flex items-center gap-2 text-[13px] text-slate-700 cursor-pointer" data-testid="auto-convert-toggle">
            <input
              type="checkbox"
              checked={!!prefs.auto_convert_on_approve}
              disabled={prefs.loading}
              onChange={(e) => toggleAutoConvert(e.target.checked)}
            />
            {" "}Auto-convert approved signals to RNC
          </label>
        </header>

        {/* Live channels */}
        {LIVE_SOURCES.map((ch) => {
          const rows = grouped[ch.id] || [];
          return (
            <section key={ch.id} data-testid={`live-channel-${ch.id}`} className="min-w-0">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                  <span className={cn("inline-flex h-7 w-7 items-center justify-center rounded-lg ring-1 shrink-0", ch.ring)}>
                    <ch.Icon size={13} />
                  </span>
                  <h2 className="text-[16px] font-semibold tracking-tight text-slate-900">{ch.label}</h2>
                  <span className="text-[11px] text-slate-500">· {rows.length} {rows.length === 1 ? "source" : "sources"}</span>
                </div>
                {addingFor !== ch.id && (
                  <Button size="sm" variant="outline" className="h-10 sm:h-8 text-[11.5px] w-full sm:w-auto shrink-0" onClick={() => setAddingFor(ch.id)}
                    data-testid={`add-source-${ch.id}`}>
                    <Plus size={11} /> Add
                  </Button>
                )}
              </div>
              {addingFor === ch.id && (
                <SourceForm channel={ch} onCreate={createSource} onCancel={() => setAddingFor(null)} />
              )}
              <div className="mt-3 space-y-2">
                {state.loading && <Skeleton className="h-[60px] rounded-lg" />}
                {!state.loading && rows.length === 0 && addingFor !== ch.id && (
                  <div className="text-[12px] text-slate-400 italic px-1">
                    No {ch.label} sources yet. Add one to get started.
                  </div>
                )}
                {rows.map((s) => <SourceRow key={s.id} source={s} onReload={load} />)}
              </div>
            </section>
          );
        })}

        {/* v2 — Coming soon */}
        <section data-testid="v2-section" className="pt-4 border-t border-slate-100">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between mb-3">
            <h2 className="text-[16px] font-semibold tracking-tight text-slate-900">Coming next</h2>
            <span className="text-[11px] text-slate-500">vote to influence the roadmap</span>
          </div>
          {v2.loading ? <Skeleton className="h-[140px] rounded-xl" /> : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
              {v2.sources.map((tile) => (
                <V2Tile key={tile.id} tile={tile} voted={voted.has(tile.id)} onVote={() => voteFor(tile.id)} />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
